import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "aws-amplify";
import { OutlineType } from "../brief/types";
import { ProjectType } from "../projects";
import { PlanType } from "./types";

import * as Sentry from "@sentry/nextjs";

export const createDoc = createAsyncThunk(
  "utils/createDoc",
  async (body: {
    title: string;
    seoTitle: string;
    seoDescription: string;
    keyword: string;
    searchVolume: string | number;
    secondaryKeywords: string;
    wordCount: number;
    suggestedNumberOfHeadings: number;
    numberOfWordsPerHeadings: string;
    images: number;
    headings: OutlineType[];
    statistics: Array<any>;
    insights: string;
    brandVoice: ProjectType["brandVoice"];
    companyDetails: ProjectType["companyDetails"];

  }) => {
    return await API.post("utils", `/utils/gdoc/createBrief`, {
      body,
    });
  }
);

export const getSites = createAsyncThunk(
  "utils/getSites",
  async (body: {
    projectDomain?: string;
    projectId?: string;
  }) => {
    const response = await API.post("utils", `/utils/gsc/getSites`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

export const getSearchConsoleData = createAsyncThunk(
  "utils/getSearchConsoleData",
  async (body: {
    siteUrl: string;
    projectId: string;
  }) => {
    const response = await API.post("utils", `/utils/gsc/enable`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

export const disableSearchConsole = createAsyncThunk(
  "utils/disableSearchConsole",
  async (body: {
    projectDomain: string;
    projectId: string;
  }) => {
    const response = await API.post("utils", `/utils/gsc/disable`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

export const checkKeywordRanking = createAsyncThunk(
  "utils/checkKeywordRanking",
  async (body: {
    keyword: string;
    GSCsiteUrl: string;
    projectId: string;
  }) => {
    try {
      const response = await API.post("utils", `/utils/gsc/checkKeywordRanking`, {
        body,
      });
      if (response.error) {
        Sentry.captureException(response.error);
      }
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error checking keyword ranking:", error);
      return { error: true, message: String(error) };
    }
  }
);

export const getPlans = createAsyncThunk("system/getPlans", async () => {
  try {
    const response = await API.get("plans", `/plans`, {});
    return response;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error fetching plans:", error);
    return { error: true };
  }
});

export const getAllPaddleUsers = createAsyncThunk(
  "system/getAllPaddleUsers",
  async ({ filters, limit, paginationToken }: Record<string, any>) => {
    try {
      return await API.get(
        "users",
        `/users?limit=${limit}${filters.query ? `&email=${filters.query}` : ""}${paginationToken
          ? `&paginationTokenEmail=${encodeURIComponent(
            paginationToken.email
          )}&paginationTokenCreatedAt=${encodeURIComponent(
            paginationToken.createdAt
          )}&paginationTokenStatus=${encodeURIComponent(
            paginationToken.status
          )}`
          : ""
        }`,
        {}
      );
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "system/getCurrentUser",
  async () => {
    try {
      return await API.get("users", `/users/current`, {});
    }
    catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const createPlan = createAsyncThunk<PlanType, Partial<PlanType>>(
  "plans/create",
  async (data) => {
    try {
      const response = await API.post("plans", "/plans", {
        body: data,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);
export const changeCurrentPlan = createAsyncThunk(
  "user/changePlan",
  async (data: { planId: string }) => {
    try {
      const response = await API.put("users", "/users/changePlan", {
        body: data,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getPlanDetails = createAsyncThunk(
  "user/getPlan",
  async (data: { planId: string }) => {
    try {
      const response = await API.put("users", "/users/getPlan", {
        body: data,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const getCheckoutLink = createAsyncThunk(
  "user/getCheckoutLink",
  async (data: { planId: string }) => {
    try {
      const response = await API.put("users", "/users/checkout", {
        body: data,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const cancelPlan = createAsyncThunk(
  "user/cancelPlan",
  async (id: { id: string }) => {
    try {
      const subscriptionId = id.id;
      const response = await API.put("users", "/users/cancelPlan", {
        body: { subscriptionId },
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//user upgrade plan from trial to paid
export const upgradePlan = createAsyncThunk(
  "user/upgradePlan",
  async () => {
    try {
      const response = await API.put("users", "/users/upgradePlan", {

      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);
export const updatePlan = createAsyncThunk<PlanType, Partial<PlanType>>(
  "plans/update",
  async (data) => {
    try {
      const response = await API.put("plans", `/plans/${data.id}`, {
        body: data,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

export const updateUserPlan = createAsyncThunk(
  "users/update",
  async ({ email, ...plan }: Partial<PlanType & { email: string } & { briefCount: number } & { articleCount: number } & { AIwordsCount: number }>) => {
    try {
      let body = {
        plan: {},
        briefCount: 0,
        articleCount: 0,
        AIwordsCount: 0
      };
      if (plan.next_bill_date) {
        body = {
          plan: {
            ...plan,
            type: "paddle",
            event_time: new Date().toISOString(),
          },
          briefCount: 0,
          articleCount: 0,
          AIwordsCount: 0



        };
      }

      const response = await API.put("users", `/users/${email}`, {
        body,
      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//update user isOnboardingShown value
export const updateUserOnboarding = createAsyncThunk(
  "user/updateOnboarding",
  async () => {
    try {
      const response = await API.put("users", "/users/updateOnboarding", {

      });
      return response;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
    }
  }
);

//fucntion to save top bar values in backend message, link and CTA and a randomly generated id
export const saveTopBar = createAsyncThunk(
  "utils/saveTopBar",
  async (body: {
    message: string;
    link: string;
    CTA: string;
    topBarId: string;
  }

  ) => {
    const response = await API.post("utils", `/utils/topbar/save`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

//get top bar values from backend
export const getTopBar = createAsyncThunk(
  "utils/getTopBar",
  async () => {
    try {
      const response = await API.get("utils", `/utils/topbar/get`, {});
      return response;
    } catch (error) {
      Sentry.captureException(error);
      console.error("Error fetching top bar values:", error);
      return { error: true };
    }
  }
);

//function for contet gap analysis
export const createContentGapAnalysis = createAsyncThunk(
  "utils/getContentGap",
  async (body: {
    domain: string;
    competitorDomains: string[];
  }) => {
    const response = await API.post("utils", `/utils/contentGapAnalysis`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);
//download csv
export const downloadCsv = createAsyncThunk(
  "utils/downloadCsv",
  async () => {
    const response = await API.get("utils", `/utils/downloadCsv`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "contentGapAnalysis.csv");
    document.body.appendChild(link);
    link.click();
  }
);

//update user details
export const updateUserDetails = createAsyncThunk(
  "utils/updateUserDetails",
  async (body: {
    email: string;
    username: string;
  }) => {
    const response = await API.put("utils", `/utils/user/update`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

//send invite to team members
export const sendInvite = createAsyncThunk(
  "user/sendInvite",
  async (body: { email: string, ownerEmail: string, teamId: string, role: string }) => {

    const response = await API.post("users", `/users/sendInvite`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

//get team members
export const getTeamMembers = createAsyncThunk(
  "user/getTeamMembers",
  async (body: { email: string }) => {
    const response = await API.post("users", `/users/teamMembers`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

//change role of team member
export const changeRole = createAsyncThunk(
  "user/changeRole",
  async (body: { email: string, role: string }) => {
    const response = await API.post("users", `/users/changeRole`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);

//delete team member
export const deleteTeamMember = createAsyncThunk(
  "user/deleteTeamMember",
  async (body: { email: string }) => {
    const response = await API.post("users", `/users/deleteTeamMember`, {
      body,
    });
    if (response.error) {
      Sentry.captureException(response.error);
    }
    return response;
  }
);